import { Outlet, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { BackButton, StepDots, TopBar } from '../../primitives'

const STEP_ORDER = ['game', 'players', 'results', 'details', 'confirm'] as const
type Step = (typeof STEP_ORDER)[number]

const TITLES: Record<Step, string> = {
  game: 'Pick a game',
  players: 'Pick players',
  results: 'Enter results',
  details: 'Optional details',
  confirm: 'Save play',
}

const stepFromPath = (path: string): Step => {
  const last = path.split('/').filter(Boolean).pop() ?? 'game'
  return (STEP_ORDER as readonly string[]).includes(last) ? (last as Step) : 'game'
}

export const GroupRecordPlayLayout = () => {
  const { id } = useParams({ from: '/group/$id/record' })
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const step = stepFromPath(pathname)
  const stepIndex = STEP_ORDER.indexOf(step) + 1
  const prevStep = stepIndex > 1 ? STEP_ORDER[stepIndex - 2] : null

  // The arrow pops in-app history (true Back). The fallback only fires
  // on a deep link / reload straight onto a step, where there's no
  // history: step 2+ goes one step earlier, step 1 exits to group home.
  const back = (
    <BackButton
      fallback={() =>
        prevStep
          ? navigate({ to: `/group/$id/record/${prevStep}`, params: { id } })
          : navigate({ to: '/group/$id', params: { id } })
      }
    />
  )

  return (
    <div className="paper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar left={back} title={TITLES[step]} sub={`Step ${stepIndex} of ${STEP_ORDER.length}`} />
      <StepDots total={STEP_ORDER.length} current={stepIndex} />
      <div style={{ flex: 1, padding: '4px 16px 24px' }}>
        <Outlet />
      </div>
    </div>
  )
}

export const NoGroupRecordPlayLayout = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // The /record index is the scope picker, which renders its own full
  // page — no wizard TopBar/StepDots chrome.
  const lastSegment = pathname.split('/').filter(Boolean).pop() ?? ''
  if (!(STEP_ORDER as readonly string[]).includes(lastSegment)) {
    return <Outlet />
  }

  const step = stepFromPath(pathname)
  const stepIndex = STEP_ORDER.indexOf(step) + 1
  const prevStep = stepIndex > 1 ? STEP_ORDER[stepIndex - 2] : null

  const back = (
    <BackButton
      fallback={() =>
        prevStep ? navigate({ to: `/record/${prevStep}` }) : navigate({ to: '/home' })
      }
    />
  )

  return (
    <div className="paper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar
        left={back}
        title={TITLES[step]}
        sub={`No group · Step ${stepIndex} of ${STEP_ORDER.length}`}
      />
      <StepDots total={STEP_ORDER.length} current={stepIndex} />
      <div style={{ flex: 1, padding: '4px 16px 24px' }}>
        <Outlet />
      </div>
    </div>
  )
}
